import { Box, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import gaiaTheme from '../../../root-components/theme';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormToggleInputDisplay } from '../form-field-components/new-and-edit/form-toggle-input';
import { ReportContentArea } from '../../../modules/public-reports/shared-services/gaia-report-content-schema';

/** Read excluded-section keys off a settings object, tolerating the legacy `exludedSections` spelling and stringified arrays. */
const readExcluded = (settings: any): string[] => {
  const raw = settings?.excludedSections ?? settings?.exludedSections;
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return Array.isArray(raw) ? raw : [];
  }
};

/**
 * Two-pane report content editor: section list on the left, the selected
 * section's wording on the right. Shared by the Carbon Summary and Project
 * report "Content" tabs.
 *
 * The right-hand fields are the app's own `FormTextInputDisplay` /
 * `FormToggleInputDisplay` rows (display-then-edit: read row + pencil that flips
 * to an inline input, saved via the row's own tick — no floating buttons), so
 * they match the Cover/Colours rows on the Design tab. Visibility persists to
 * `excludedSections`; wording persists to `sectionContent`. A blank field falls
 * back to the built-in default (shown as the greyed placeholder).
 */
export const ReportContentEditor: FunctionComponent<{
  areas: ReportContentArea[];
  settings: any;
  /** Persists a single settings field (returns the updated settings). */
  saveField: (field: string, value: any) => Promise<any>;
}> = ({ areas, settings, saveField }) => {
  const isMobile = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const excluded = useMemo(() => readExcluded(settings), [settings]);
  const savedContent: Record<string, string> = useMemo(() => settings?.sectionContent || {}, [settings]);

  const [selected, setSelected] = useState(0);
  const [editField, setEditField] = useState<string>('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Drop any in-progress edit when the persisted settings change (e.g. after a save).
  useEffect(() => {
    setEditField('');
    setDrafts({});
  }, [settings]);

  const area = areas[Math.min(selected, areas.length - 1)];
  const isExcluded = (sectionKey: string | null) => Boolean(sectionKey) && excluded.includes(sectionKey as string);

  const toggleSection = (sectionKey: string, checked: boolean) => {
    const next = checked ? excluded.filter((k) => k !== sectionKey) : Array.from(new Set([...excluded, sectionKey]));
    saveField('excludedSections', next);
  };

  const blockFor = (key: string) => area?.blocks.find((b) => b.key === key);
  const savedValue = (key: string) => {
    const field = blockFor(key)?.settingsField;
    return field ? (settings?.[field] ?? '') : (savedContent[key] ?? '');
  };
  const fieldValue = (key: string) => (editField === key ? (drafts[key] ?? savedValue(key)) : savedValue(key));

  const cancelEditHandler = () => {
    if (editField) {
      setDrafts((d) => {
        const next = { ...d };
        delete next[editField];
        return next;
      });
    }
    setEditField('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const key = editField;
    if (!key) return;
    const value = (drafts[key] ?? savedValue(key)).trim();
    const field = blockFor(key)?.settingsField;
    if (field) {
      // Cover title/year live in their own settings column, not the override map.
      await saveField(field, value || null);
    } else {
      const merged: Record<string, string> = { ...savedContent };
      if (value) merged[key] = value;
      else delete merged[key];
      await saveField('sectionContent', merged);
    }
    setEditField('');
  };

  const tokenTooltip = (tokens: string[]) =>
    tokens.length ? `Tokens you can type in the text: ${tokens.map((t) => `{{${t}}}`).join(', ')}` : undefined;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'lightGrey.main', borderRadius: '10px', overflow: 'hidden' }}>
      <Stack direction={isMobile ? 'column' : 'row'} sx={{ minHeight: isMobile ? undefined : 460 }}>
        {/* Left rail: section list */}
        <Box sx={{ width: isMobile ? '100%' : 248, flexShrink: 0, borderRight: isMobile ? 0 : '1px solid', borderBottom: isMobile ? '1px solid' : 0, borderColor: 'lightGrey.main', backgroundColor: 'lightGrey.light' }}>
          <Tabs
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            value={selected}
            onChange={(_, v) => { setSelected(v); cancelEditHandler(); }}
            sx={{
              '& .MuiTab-root': {
                alignItems: isMobile ? 'center' : 'flex-start',
                textTransform: 'none',
                minHeight: 48,
                fontWeight: 400,
                fontSize: '0.9rem',
                textAlign: 'left',
                color: 'text.secondary',
                px: 2.5,
              },
              '& .MuiTab-root.Mui-selected': { color: 'primary.dark', fontWeight: 600, backgroundColor: 'background.paper' },
              '& .MuiTabs-indicator': { left: 0, width: isMobile ? undefined : 3, backgroundColor: 'primary.main' },
            }}
          >
            {areas.map((a) => (
              <Tab
                key={a.label}
                label={
                  <Stack direction='row' alignItems='center' spacing={0.75} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                    <Typography variant='body2' sx={{ fontWeight: 'inherit', color: 'inherit' }}>{a.label}</Typography>
                    {isExcluded(a.sectionKey) && <VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Right panel: selected section editor */}
        <Box component='form' onSubmit={handleSubmit} sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
          {area && (
            <>
              <Typography variant='h6' sx={{ fontWeight: 600, color: 'text.primary' }}>{area.label}</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2.5 }}>
                {area.note ? `${area.note} ` : ''}Leave a field blank to keep the built-in wording.
              </Typography>

              {area.sectionKey && (
                <Box sx={{ mb: 3 }}>
                  <FormToggleInputDisplay
                    isEditPage
                    allowEdit={false}
                    disabled={false}
                    title='Show in report'
                    name={`show-${area.sectionKey}`}
                    value={!isExcluded(area.sectionKey)}
                    onChange={(e) => toggleSection(area.sectionKey as string, e.target.checked)}
                    isTopRow
                    isBottomRow
                  />
                </Box>
              )}

              {area.blocks.map((block, index) => (
                <FormTextInputDisplay
                  key={block.key}
                  isEditPage
                  editField={editField}
                  setEditField={setEditField}
                  disabled={editField !== block.key}
                  title={block.label}
                  name={block.key}
                  value={fieldValue(block.key)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDrafts((d) => ({ ...d, [block.key]: e.target.value }))}
                  cancelEditHandler={cancelEditHandler}
                  multiline={block.kind !== 'heading'}
                  rows={block.kind === 'heading' ? undefined : 3}
                  placeholder={block.placeholder}
                  tooltip={tokenTooltip(block.tokens)}
                  isTopRow={index === 0}
                  isBottomRow={index === area.blocks.length - 1}
                />
              ))}
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
